/**
 * SankeyChart Component
 *
 * Visualizes flow data using Sankey diagrams showing paths between states.
 * Works with data from flow queries which provide nodes and links.
 *
 * The chart displays:
 * - Nodes representing events at different layers (before, start, after)
 * - Links showing the flow of entities between adjacent nodes
 * - Values indicating count of entities following each path
 */

import React, { useMemo, useRef, useState, useEffect } from 'react'
import { useTranslation } from '../../hooks/useTranslation.js'
import { Sankey, Tooltip, ResponsiveContainer } from 'recharts'
import { CHART_COLORS } from '../../utils/chartConstants.js'
import type { ChartProps } from '../../types.js'
import type { FlowChartData } from '../../types/flow.js'
import { isSankeyData } from '../../types/flow.js'

/**
 * Color palette for Sankey nodes based on layer
 */
const SANKEY_COLORS = {
  before: '#F97316', // orange-500 - for steps before
  start: '#3B82F6', // blue-500 - for starting step
  after: '#10B981', // emerald-500 - for steps after
}

/**
 * Get color for a node based on its layer
 */
function getNodeColor(layer: number, colorPalette?: string[]): string {
  if (colorPalette && colorPalette.length > 0) {
    // Use palette colors cycling through
    const absLayer = Math.abs(layer)
    return colorPalette[absLayer % colorPalette.length]
  }

  if (layer < 0) return SANKEY_COLORS.before
  if (layer === 0) return SANKEY_COLORS.start
  return SANKEY_COLORS.after
}

/**
 * Transform FlowChartData to Recharts Sankey format
 */
function transformToRechartsFormat(
  data: FlowChartData,
  colorPalette?: string[]
): {
  nodes: Array<{ name: string; fill: string; layer: number; value?: number }>
  links: Array<{ source: number; target: number; value: number }>
} | null {
  if (!data.nodes || data.nodes.length === 0) return null

  // Create node index map for link resolution
  const nodeIndexMap = new Map<string, number>()
  data.nodes.forEach((node, index) => {
    nodeIndexMap.set(node.id, index)
  })

  // Transform nodes
  const nodes = data.nodes.map((node) => ({
    name: node.name,
    fill: getNodeColor(node.layer, colorPalette),
    layer: node.layer,
    value: node.value,
  }))

  // Transform links - resolve string IDs to numeric indices
  const links = data.links
    .map((link) => {
      const sourceIndex = nodeIndexMap.get(link.source)
      const targetIndex = nodeIndexMap.get(link.target)

      if (sourceIndex === undefined || targetIndex === undefined) {
        console.warn(`Sankey: Could not resolve link ${link.source} -> ${link.target}`)
        return null
      }

      return {
        source: sourceIndex,
        target: targetIndex,
        value: link.value,
      }
    })
    .filter((link): link is { source: number; target: number; value: number } => link !== null)

  return { nodes, links }
}

/**
 * Extract FlowChartData from various data formats
 */
function extractFlowData(data: unknown[]): FlowChartData | null {
  if (!data || data.length === 0) return null

  // If first element is already FlowChartData (server returns as single-row array)
  if (isSankeyData(data[0])) {
    return data[0] as FlowChartData
  }

  // If data itself is FlowChartData shape
  if (isSankeyData(data)) {
    return data as unknown as FlowChartData
  }

  // Check if data is an array with nodes and links at top level
  const asAny = data as unknown as Record<string, unknown>
  if (asAny.nodes && asAny.links) {
    return asAny as unknown as FlowChartData
  }

  return null
}

/**
 * Custom node renderer for Sankey chart
 * Displays the node rectangle with label and value
 */
function SankeyNode({
  x,
  y,
  width,
  height,
  payload,
  containerWidth,
  showLabels = true,
}: {
  x: number
  y: number
  width: number
  height: number
  payload: { name: string; value?: number; fill: string }
  containerWidth: number
  showLabels?: boolean
}) {
  // Determine if label should be on the left or right based on position
  const isRightSide = x > containerWidth / 2
  const labelX = isRightSide ? x - 6 : x + width + 6
  const textAnchor = isRightSide ? 'end' : 'start'

  return (
    <g>
      {/* Node rectangle */}
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        fill={payload.fill}
        rx={2}
        ry={2}
      />
      {/* Node label - only shown if showLabels is true */}
      {showLabels && (
        <text
          x={labelX}
          y={y + height / 2}
          textAnchor={textAnchor}
          dominantBaseline="middle"
          className="dc:text-xs dc:fill-dc-text"
          style={{ fontSize: 11 }}
        >
          {payload.name}
          {payload.value !== undefined && (
            <tspan className="dc:fill-dc-text-secondary" dx={4}>
              ({payload.value.toLocaleString()})
            </tspan>
          )}
        </text>
      )}
    </g>
  )
}

/** Shared tooltip card wrapper for the Sankey tooltip variants. */
function SankeyTooltipCard({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-dc-surface dc:border border-dc-border dc:rounded-md dc:px-3 dc:py-2 dc:shadow-lg dc:text-sm">
      {children}
    </div>
  )
}

/** Entity count row shared by both tooltip variants. */
function SankeyEntityCount({ value }: { value: number }) {
  return (
    <div className="text-dc-text-secondary dc:mt-1">
      <span className="dc:font-medium">{value.toLocaleString()}</span> entities
    </div>
  )
}

interface SankeyTooltipDatum {
  name: string
  value: number
  source?: { name: string }
  target?: { name: string }
}

/**
 * Custom tooltip for Sankey chart. Renders a link variant (source → target)
 * when both endpoints are present, otherwise a node variant.
 */
function SankeyTooltip({ active, payload }: {
  active?: boolean
  payload?: Array<{ payload: SankeyTooltipDatum }>
}) {
  if (!active || !payload || payload.length === 0) return null

  const data = payload[0].payload
  const { source, target } = data

  if (source && target) {
    return (
      <SankeyTooltipCard>
        <div className="dc:font-medium text-dc-text">
          {source.name} → {target.name}
        </div>
        <SankeyEntityCount value={data.value} />
      </SankeyTooltipCard>
    )
  }

  return (
    <SankeyTooltipCard>
      <div className="dc:font-medium text-dc-text">{data.name}</div>
      {data.value !== undefined && <SankeyEntityCount value={data.value} />}
    </SankeyTooltipCard>
  )
}

/**
 * SankeyChart Component
 *
 * Renders a Sankey diagram visualization from FlowChartData.
 * Shows flow paths with nodes at each layer and links between them.
 */
const SankeyChart = React.memo(function SankeyChart({
  data,
  height = '100%',
  colorPalette,
  displayConfig,
}: ChartProps) {
  const { t } = useTranslation()
  // Track container width for label positioning
  const containerRef = useRef<HTMLDivElement>(null)
  const [containerWidth, setContainerWidth] = useState(800)

  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth)
      }
    }
    updateWidth()
    window.addEventListener('resize', updateWidth)
    return () => window.removeEventListener('resize', updateWidth)
  }, [])

  // Get display config options
  // Use custom properties from displayConfig if available, otherwise use defaults
  const displayConfigAny = displayConfig as Record<string, unknown> | undefined
  const linkOpacity = parseFloat(String(displayConfigAny?.linkOpacity || '0.5'))
  const nodeWidth = (displayConfigAny?.nodeWidth as number) ?? 10
  const nodePadding = (displayConfigAny?.nodePadding as number) ?? 50
  const showNodeLabels = displayConfigAny?.showNodeLabels !== false // Default true

  // Extract and transform data
  const flowData = useMemo(() => {
    const extracted = extractFlowData(data || [])
    if (!extracted) return null

    return transformToRechartsFormat(extracted, colorPalette?.colors || CHART_COLORS)
  }, [data, colorPalette])

  // Calculate summary stats
  const summaryStats = useMemo(() => {
    const extracted = extractFlowData(data || [])
    if (!extracted) return null

    const totalEntities = extracted.nodes
      .filter((n) => n.layer === 0)
      .reduce((sum, n) => sum + (n.value || 0), 0)

    const totalPaths = extracted.links.reduce((sum, l) => sum + l.value, 0)

    return {
      nodeCount: extracted.nodes.length,
      linkCount: extracted.links.length,
      totalEntities,
      totalPaths,
    }
  }, [data])

  // Handle no data
  if (!data || data.length === 0 || !flowData || flowData.nodes.length === 0) {
    return (
      <div
        className="dc:flex dc:items-center dc:justify-center dc:w-full text-dc-text-muted"
        style={{ height }}
      >
        <div className="dc:text-center">
          <div className="dc:text-sm dc:font-semibold dc:mb-1">{t('chart.runtime.flow.noData')}</div>
          <div className="dc:text-xs text-dc-text-secondary">
            {t('chart.runtime.noDataHint.flow')}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div ref={containerRef} className="dc:relative dc:w-full dc:h-full dc:flex dc:flex-col" style={{ height }}>
      {/* Sankey Diagram */}
      <div className="dc:flex-1 dc:min-h-0">
        <ResponsiveContainer width="100%" height="100%">
          <Sankey
            data={flowData}
            nodeWidth={nodeWidth}
            nodePadding={nodePadding}
            margin={{ top: 20, right: 20, bottom: 20, left: 20 }}
            link={{
              stroke: 'var(--dc-border)',
              strokeOpacity: linkOpacity,
            }}
            node={(props: any) => (
              <SankeyNode {...props} containerWidth={containerWidth} showLabels={showNodeLabels} />
            )}
          >
            <Tooltip content={<SankeyTooltip />} />
          </Sankey>
        </ResponsiveContainer>
      </div>

      {/* Summary Footer */}
      {!(displayConfigAny?.hideSummaryFooter) && summaryStats && (
        <div className="dc:flex-shrink-0 dc:px-4 dc:py-2 dc:border-t border-dc-border bg-dc-surface-secondary">
          <div className="dc:flex dc:items-center dc:justify-between dc:text-sm">
            <div className="text-dc-text-muted">
              <span className="dc:font-medium">{summaryStats.nodeCount}</span> events
            </div>
            <div className="text-dc-text">
              <span className="text-dc-text-muted">{t('chart.runtime.flow.paths')}</span>{' '}
              <span className="dc:font-medium">{summaryStats.linkCount}</span>
            </div>
            <div className="text-dc-text-muted">
              <span className="dc:font-medium">{summaryStats.totalEntities.toLocaleString()}</span> starting entities
            </div>
          </div>
        </div>
      )}
    </div>
  )
})

export default SankeyChart
